/**
 * FIX-274/FIX-281: parts leave and re-enter stock only via the
 * take_out_spare_part / undo_take_out RPCs; the permission rules live in SQL.
 * This file maps their refusal codes and labels movement rows.
 */
import { supabase } from "@/integrations/supabase/client";
import type { ExitStatus } from "@/lib/partStatus";

export const TAKE_OUT_ERROR_CODES = [
  "not_allowed",
  "not_found",
  "invalid_reason",
  "already_taken_out",
  "not_in_stock",
  "not_taken_out",
  "invalid_bin",
] as const;
export type TakeOutErrorCode = (typeof TAKE_OUT_ERROR_CODES)[number];

export const UNDO_NOTE = "Undo take-out";

export function takeOutErrorCode(message: string | null | undefined): TakeOutErrorCode | "unknown" {
  const m = (message ?? "").trim();
  return (TAKE_OUT_ERROR_CODES as readonly string[]).includes(m) ? (m as TakeOutErrorCode) : "unknown";
}

export interface MovementRow {
  id: string;
  type: "in" | "out" | "move";
  reason: ExitStatus | null;
  bin_id: string | null;
  note: string | null;
  performed_by: string;
  performed_at: string;
  backfilled: boolean;
}

export type MovementKind = "putInBin" | "moved" | "takenOut" | "putBack";

export function movementKind(m: Pick<MovementRow, "type" | "note">): MovementKind {
  if (m.type === "out") return "takenOut";
  if (m.type === "move") return "moved";
  return m.note === UNDO_NOTE ? "putBack" : "putInBin";
}

/** Bin the part left in the newest 'out' movement (rows newest first or any order). */
export function lastOutBinId(rows: MovementRow[]): string | null {
  const outs = rows.filter((r) => r.type === "out").sort((a, b) => b.performed_at.localeCompare(a.performed_at));
  return outs[0]?.bin_id ?? null;
}

async function rpcOrThrow(fn: string, args: Record<string, unknown>): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)(fn, args);
  if (error) throw new Error(takeOutErrorCode(error.message));
  return data as string;
}

export const takeOutSparePart = (partId: string, reason: ExitStatus, note: string) =>
  rpcOrThrow("take_out_spare_part", { _spare_part_id: partId, _reason: reason, _note: note.trim() || null });

export const undoTakeOut = (partId: string, binId: string | null) =>
  rpcOrThrow("undo_take_out", { _spare_part_id: partId, _bin_id: binId });
